/**
 * Wire shapes for the Book/Draft -> Project mapping backfill.
 *
 * Canonical contract: docs/contracts/shared/ProjectMappingReview.md.
 *
 * Two things in here are load-bearing and easy to undo by accident:
 *
 * 1. **No confidence number ever crosses the wire.** The server sends `band`
 *    and `needsDecision` and nothing finer. There is deliberately no
 *    `confidence` field below, and adding one later would mean a customer
 *    reading "71%" off a screen and arguing with it.
 *
 * 2. **Reasons are codes, not prose.** The server never sends English. Every
 *    code here is turned into display text by the client, because the client
 *    is what has 21 locales. `ProjectMappingVocabulary` is the only place that
 *    mapping lives.
 *
 * No Angular import in this file: it is a model, and the smoke test
 * (tests/ProjectMapping.smoke.ts) loads it under plain ts-node.
 */

/** Confidence band. The only grading the client is ever told. */
export type ProjectMappingBand = 'SINGLETON' | 'CONFIDENT' | 'REVIEW';

/** Why a proposal needs a human answer before it can be applied. */
export type ProjectMappingDecisionReason = 'LOW_CONFIDENCE' | 'PRIMARY_AMBIGUOUS' | 'PRIMARY_STALE';

/** Why one member is in the cluster. Per member, never per cluster. */
export type ProjectMappingJoinReason =
  | 'SEED'
  /**
   * Every compared facet agrees - title, author, interior PDF, cover PDF,
   * pages, trim, and the published identifier where both items carry one.
   *
   * Added by planner 1.2.0 and now the **commonest thing a card says** (3,764
   * occurrences across the estate, against 606 for `BODY_RESOURCE_EXACT`).
   * It is computed at explanation time, not an edge weight.
   *
   * It exists because the previous answer was misleading by omission:
   * `BODY_RESOURCE_EXACT` claims only the interior, pages and trim, so two
   * items agreeing on *everything* were described as agreeing on three things.
   */
  | 'IDENTICAL'
  | 'WORK_ID_EXISTING'
  | 'ISBN_EXACT'
  | 'SFBN_EXACT'
  | 'BODY_RESOURCE_EXACT'
  | 'BODY_RESOURCE_TITLE'
  | 'BODY_RESOURCE_ONLY'
  | 'TITLE_AUTHOR_PAGES'
  | 'TITLE_AUTHOR'
  | 'TITLE_EXACT'
  | 'TITLE_CORE'
  | 'COVER_RESOURCE'
  | 'TITLE_FUZZY';

/** Which rule proposed this member as the Project primary. */
export type ProjectMappingPrimaryRule = 'RECENCY' | 'ID';

export type ProjectMappingItemType = 'BOOK' | 'DRAFT';

export type ProjectMappingProposalStatus =
  | 'PROPOSED' | 'ACCEPTED' | 'REJECTED' | 'EDITED' | 'APPLIED' | 'SUPERSEDED' | 'FAILED';

export type ProjectMappingOrganizationState =
  | 'NOT_PLANNED' | 'PLAN_STALE' | 'READY_SIMPLE' | 'READY_PARTIAL'
  | 'AWAITING_CUSTOMER' | 'CUSTOMER_DECIDED' | 'COMPLETE';

export type ProjectMappingRunPhase =
  | 'PLANNING' | 'PLANNED' | 'APPLYING' | 'APPLIED' | 'REVERTING' | 'REVERTED' | 'FAILED';

export type ProjectMappingApplySelector = 'ALL' | 'NO_DECISION_NEEDED' | 'DECIDED' | 'IDS';

export type ProjectMappingDecisionValue = 'ACCEPTED' | 'REJECTED' | 'EDITED';

/** An item reference. Ids only -- see `ProjectMappingReview.md` §8.1. */
export interface ProjectMappingItemRef {
  itemType: ProjectMappingItemType;
  item_ID: number;
}

/**
 * The handful of properties this surface actually renders, sent inline.
 *
 * ## Why this exists
 *
 * Without it the client resolves every id against the Book and Draft caches
 * and fetches whatever is missing **one at a time**. On this screen that is
 * the common case rather than the edge: a review payload is by construction
 * full of unmapped Books (`work_ID = 0`, no Product, no recent order), which
 * is exactly the population `project.books[]` cannot reach -- see §8.1, which
 * concedes the point in as many words ("forty single fetches is a worse way to
 * load forty Books than one filtered call").
 *
 * So this is not a convenience. It is the difference between one request and
 * N+1 for a screen whose entire job is to show the reader the values it is
 * asking them to compare.
 *
 * ## Deliberately NOT a Book
 *
 * Every field below is on screen somewhere: the eight facet rows plus the card
 * title. Nothing else belongs here. This endpoint must not quietly become a
 * general-purpose Book reader -- if a new value is wanted, add the facet first
 * and the field second, so the payload stays traceable to something visible.
 *
 * ## Optional, and must stay optional
 *
 * `display` is absent on any server that has not shipped it yet, and the
 * client falls back to the cache-and-fetch path unchanged. Treat a missing
 * block as "not sent", never as "this item has no title".
 *
 * Geometry is sent in POINTS, as the Body/DraftBody models store it. Formatting
 * it into inches -- and localizing the digits -- is the client's job, because
 * the server has no locale.
 */
export interface ProjectMappingItemDisplay {
  title?: string;
  author?: string;
  /** Interior PDF. `body.resource_ID` / `draftBody.resource_ID`. */
  bodyResource_ID?: number;
  /** Cover PDF. `cover.resource_ID` / `draftCover.resource_ID`. */
  coverResource_ID?: number;
  numPages?: number;
  /** Trim, in points. */
  trimWidth?: number;
  trimHeight?: number;
  /** Book only; Drafts carry no identifier. `sfbn` when `isbn` is unset. */
  isbn?: string;
  /** The Project this item already belongs to, or 0. */
  work_ID?: number;
  /**
   * Physical specification. Raw codes and an id, never display text - the
   * client holds the 21 locales and `PaperTypeService` resolves the name.
   *
   * `bindingType` is on the Book/Draft, `paperType_ID` on the Body/DraftBody,
   * `laminateType` on the Cover/DraftCover - three different rows, which is
   * exactly why they are wanted inline. Requested 2026-10-26g.
   */
  bindingType?: string;
  paperType_ID?: number;
  laminateType?: string;
  /** ISO 8601. Lets the reader judge PRIMARY_STALE for themselves. */
  lastUpdate?: string;
}

/**
 * Why a near miss was NOT included.
 *
 * Requested 2026-10-26. Until the server sends it the client derives a much
 * weaker answer from the connection code alone -- see `mapping-facets.ts`,
 * which is deliberately vague in every case the contract does not license it
 * to be specific about.
 */
export type ProjectMappingRejectedReason =
  | 'CORROBORATION_ONLY'
  | 'BELOW_THRESHOLD'
  | 'CONFLICTING_EVIDENCE'
  | 'PARTITION_MISMATCH'
  | 'ALREADY_MAPPED'
  | 'CLAIMED_BY_STRONGER'
  | 'CLUSTER_FULL'
  /**
   * Declared so a switch can be exhaustive, but **never emitted** (server,
   * 2026-10-26b §3). Deciding that two clusters matched equally well needs a
   * global view the clusterer does not keep, and a guessed answer there is
   * worse than an honest `BELOW_THRESHOLD`.
   *
   * Do not build anything that depends on seeing it. The server will send a
   * message before it ever starts emitting this.
   */
  | 'AMBIGUOUS_TARGET';

export interface ProjectMappingMember extends ProjectMappingItemRef {
  joinReason: ProjectMappingJoinReason;
  /** The specific sibling this member matched. `null` for the SEED. */
  joinedTo: ProjectMappingItemRef | null;
  isProposedPrimary: boolean;
  primaryRule?: ProjectMappingPrimaryRule;
  excluded: boolean;
  /** Optional inline properties. See {@link ProjectMappingItemDisplay}. */
  display?: ProjectMappingItemDisplay;
}

/**
 * Something CONSIDERED for this cluster and left out.
 *
 * `reason` is what CONNECTED it; `rejectedReason` is why that was not enough.
 * The two are different questions and the second is the one a reader argues
 * with, which is why it is being asked for separately rather than overloading
 * `reason`.
 */
export interface ProjectMappingNearMiss extends ProjectMappingItemRef {
  reason: ProjectMappingJoinReason;
  /** Optional until the server ships it; derived client-side meanwhile. */
  rejectedReason?: ProjectMappingRejectedReason;
  /**
   * The proposal that took this item instead.
   *
   * Only meaningful with `ALREADY_MAPPED` / `CLAIMED_BY_STRONGER`, and the
   * single most useful thing to show for them: "it went there" answers the
   * question, where "it was rejected" merely raises it.
   */
  rejectedTo?: { proposalId: number, proposedName?: string };
  /** Optional inline properties. See {@link ProjectMappingItemDisplay}. */
  display?: ProjectMappingItemDisplay;
}

export interface ProjectMappingProposal {
  proposalId: number;
  clusterKey: string;
  proposedName: string;
  band: ProjectMappingBand;
  needsDecision: boolean;
  decisionReasons: ProjectMappingDecisionReason[];
  status: ProjectMappingProposalStatus;
  createdProject_ID: number;
  /**
   * Which §6a structural rules cleared this proposal, distinct, across the
   * whole chain. Absent when nothing cleared it.
   *
   * Planner 1.4.0. This is the "why was I not asked?" answer, and it exists
   * because an operator spot-checking auto-approvals should not have to
   * reconstruct the reasoning from the facet grid.
   *
   * Typed loosely on purpose: the vocabulary is the server's and may grow, and
   * an unknown code renders as itself rather than crashing - the same failure
   * mode as `joinReason`.
   */
  clearedBy?: string[];

  members: ProjectMappingMember[];
  /**
   * Considered and rejected, STRONGEST FIRST and capped by the server.
   *
   * Ordering and a cap both matter: a common title ("Poems") can connect to
   * hundreds of items, and an uncapped list would dwarf the members it was
   * rejected in favour of and bloat the payload for no reader benefit.
   */
  nearMisses?: ProjectMappingNearMiss[];
  /** How many further near misses were cut by that cap. 0 or absent if none. */
  nearMissesTruncated?: number;
}

export interface ProjectMappingCounts {
  needsDecision: number;
  confident: number;
  singleton: number;
  applied: number;
}

export interface ProjectMappingReview {
  org_ID: number;
  runId: string;
  phase: ProjectMappingRunPhase;
  state: ProjectMappingOrganizationState;
  counts: ProjectMappingCounts;
  proposals: ProjectMappingProposal[];
}

export interface ProjectMappingRunSummary {
  runId: string;
  phase: ProjectMappingRunPhase;
  planned?: string;
  plannedByRole?: string;
  plannedById?: number;
  /** Present on PROJECT_MAPPING_RUN progress messages; absent on the dashboard row. */
  progress?: number;
  org_ID?: number;
}

export interface ProjectMappingProposalCounts {
  singleton: number;
  confident: number;
  review: number;
  needsDecision: number;
  decidedAwaitingApply: number;
  applied: number;
  superseded: number;
  failed: number;
}

export interface ProjectMappingDashboardRow {
  org_ID: number;
  name: string;
  serviceProvider_ID: number;
  /**
   * Primary organization user's email address.
   *
   * NOT part of the §3.1 payload -- the server sends no email on this row. It
   * is joined in client-side from `organization/activeForRole`, which is the
   * same source `SelectRoleComponent` searches, so that an operator can find
   * a customer on this screen by the same three things they can find them by
   * in the role picker. Absent when that call has not landed yet, failed, or
   * the organization has no admin user.
   */
  adminEmail?: string;
  state: ProjectMappingOrganizationState;
  unmappedBooks: number;
  unmappedDrafts: number;
  totalBooks: number;
  totalDrafts: number;
  /** `null` when the organization has never been planned. */
  run: ProjectMappingRunSummary | null;
  proposals: ProjectMappingProposalCounts;
}

export interface ProjectMappingDashboard {
  organizations: ProjectMappingDashboardRow[];
}

export interface ProjectMappingDecision {
  proposalId: number;
  decision: ProjectMappingDecisionValue;
  excludedMembers?: ProjectMappingItemRef[];
  primaryBook_ID?: number;
  primaryDraft_ID?: number;
}

export interface ProjectMappingDecideRequest {
  runId: string;
  decisions: ProjectMappingDecision[];
}

export interface ProjectMappingApplyRequest {
  selector: ProjectMappingApplySelector;
  proposalIds?: number[];
}

/**
 * Why one entry of a decide request was not recorded.
 *
 * `UNKNOWN_PROPOSAL` is the one that happens in the field: the plan was
 * regenerated under the reader, so the id they answered no longer exists.
 * `UNKNOWN_DECISION` can only be OUR bug -- the client sends a value from
 * {@link ProjectMappingDecisionValue} or nothing at all -- so it is reported
 * as a failure rather than explained to the user.
 */
export type ProjectMappingDecideRejectedReason = 'UNKNOWN_PROPOSAL' | 'UNKNOWN_DECISION';

export interface ProjectMappingDecideRecorded {
  proposalId: number;
  clusterKey: string;
  /** What was STORED, which is not necessarily what was sent -- see below. */
  decision: ProjectMappingDecisionValue;
}

export interface ProjectMappingDecideRejected {
  proposalId: number;
  reason: ProjectMappingDecideRejectedReason;
}

/**
 * The answer to `POST /project/mapping/decide` (§3.4).
 *
 * ---------------------------------------------------------------------------
 * TWO REASONS THIS BODY MUST BE READ RATHER THAN DISCARDED
 * ---------------------------------------------------------------------------
 * Both were spelled out by the server on 2026-10-28, and this client ignored
 * the body entirely until then.
 *
 * 1. **`EDITED` is implied whatever you send.** If `excludedMembers` is
 *    non-empty, or either primary differs from the proposal, the stored
 *    decision becomes `EDITED` even though we sent `ACCEPTED` -- the record
 *    has to describe what will actually be built. Trusting the value we sent
 *    puts `ACCEPTED` on a card the server has stored as `EDITED`, and the
 *    two are not cosmetic: `DECIDED` applies both, but a reader deciding
 *    whether their exclusion survived is reading this badge.
 *
 * 2. **A bad id does not fail the request.** Entries naming an unknown
 *    proposal come back in {@link rejected} and the REST ARE STILL RECORDED,
 *    deliberately -- one stale id must not discard a customer's other
 *    answers. So a 200 does not mean "all of it landed", and an unexamined
 *    200 shows a saved tick on an answer that was never stored.
 */
export interface ProjectMappingDecideResponse {
  runId: string;
  org_ID: number;
  recorded: ProjectMappingDecideRecorded[];
  rejected: ProjectMappingDecideRejected[];
  /** Recomputed, not incremented -- the dashboard's `decidedAwaitingApply`. */
  decidedAwaitingApply: number;
}

/**
 * The answer to apply (§3.5) and revert (§3.7).
 *
 * **202, not 200.** No Project exists when this arrives: both enqueue onto one
 * SQS queue and a worker does the writing. The body says what was accepted,
 * never what was built, so the only correct next move is to watch
 * {@link ProjectMappingRunResponse.phase} via §3.6.
 */
export interface ProjectMappingApplyResponse {
  runId: string;
  phase: ProjectMappingRunPhase;
  /** Echoed by apply; absent on revert. */
  selector?: ProjectMappingApplySelector;
}

/**
 * `GET /project/mapping/run/:runId` (§3.6) -- phase and progress.
 *
 * Distinct from {@link ProjectMappingRunSummary}, which is the small shape
 * carried on a dashboard row and on a `PROJECT_MAPPING_RUN` notification.
 * This one is the full read, and its `counts` is the eight-field
 * {@link ProjectMappingProposalCounts}, NOT the four-field
 * `ProjectMappingCounts` that §3.3 returns. Conflating the two would read
 * `applied` off an object that does not carry it.
 */
export interface ProjectMappingRunResponse {
  runId: string;
  org_ID: number;
  phase: ProjectMappingRunPhase;
  state: ProjectMappingOrganizationState;
  counts: ProjectMappingProposalCounts;
  /** Why a run FAILED, when it did. English; technical detail only. */
  message: string | null;
  plannedAt: string | null;
  appliedAt: string | null;
  handedToCustomerAt: string | null;
}

/**
 * Phases from which nothing more will happen without another request.
 *
 * `PLANNED` is terminal for an APPLY that was refused or that applied
 * nothing; `FAILED` is terminal and must stop the wait, or a dead run leaves
 * a spinner on screen forever.
 */
export const PROJECT_MAPPING_SETTLED_PHASES: ProjectMappingRunPhase[] =
  ['PLANNED', 'APPLIED', 'REVERTED', 'FAILED'];

/**
 * The client-side half of the shared reason vocabulary.
 *
 * Every code the server can send maps to an `@@id` that exists in all 21
 * `.xtb` bundles. The server deliberately sends no English, so a code with no
 * entry here renders as the raw code -- visible, but not a crash, which is the
 * right failure mode when the server adds a code before the client ships the
 * wording for it.
 */
export class ProjectMappingVocabulary {

  /**
   * Translation ids for `joinReason`, phrased as a reason a *reader* would
   * accept: "shares its interior PDF", not "BODY_RESOURCE_EXACT".
   */
  static readonly JOIN_REASON_IDS: { [code: string]: string } = {
    SEED:                 'JoinReasonSeed',
    IDENTICAL:            'JoinReasonIdentical',
    WORK_ID_EXISTING:     'JoinReasonWorkIdExisting',
    ISBN_EXACT:           'JoinReasonIsbnExact',
    SFBN_EXACT:           'JoinReasonSfbnExact',
    BODY_RESOURCE_EXACT:  'JoinReasonBodyResourceExact',
    BODY_RESOURCE_TITLE:  'JoinReasonBodyResourceTitle',
    BODY_RESOURCE_ONLY:   'JoinReasonBodyResourceOnly',
    TITLE_AUTHOR_PAGES:   'JoinReasonTitleAuthorPages',
    TITLE_AUTHOR:         'JoinReasonTitleAuthor',
    TITLE_EXACT:          'JoinReasonTitleExact',
    TITLE_CORE:           'JoinReasonTitleCore',
    COVER_RESOURCE:       'JoinReasonCoverResource',
    TITLE_FUZZY:          'JoinReasonTitleFuzzy',
  };

  static readonly DECISION_REASON_IDS: { [code: string]: string } = {
    LOW_CONFIDENCE:    'DecisionReasonLowConfidence',
    PRIMARY_AMBIGUOUS: 'DecisionReasonPrimaryAmbiguous',
    PRIMARY_STALE:     'DecisionReasonPrimaryStale',
  };

  /**
   * Translation ids for `rejectedReason`.
   *
   * Phrased as the answer to "why isn't this one in the group?", which is the
   * question the reader is actually holding.
   */
  /**
   * Translation ids for `clearedBy` (§6a).
   *
   * Phrased as the answer to "why was I not asked about this?". Codes
   * confirmed by the server 2026-10-27; rule A's code is a best guess pending
   * their list, and falls through to the raw-code path if wrong.
   */
  static readonly CLEARED_BY_IDS: { [code: string]: string } = {
    SHARED_BOTH_RESOURCES:      'ClearedBySharedBothResources',
    SAME_TITLE_SHARED_RESOURCE: 'ClearedBySameTitleSharedResource',
    IDENTIFIER_MATCH:           'ClearedByIdentifierMatch',
    EXISTING_PROJECT:           'ClearedByExistingProject',
    /**
     * Rule D, planner 1.6.0. An identical title clears a link on its own -
     * trim, author, identifier, lamination and page count do not override it.
     *
     * Reported only when the title is the STRONGEST thing available: a pair
     * that also shares both PDFs still reports `SHARED_BOTH_RESOURCES`,
     * because that tells an operator more.
     */
    SAME_TITLE:                 'ClearedBySameTitle',
  };

  static readonly REJECTED_REASON_IDS: { [code: string]: string } = {
    CORROBORATION_ONLY:  'NearMissCorroborationOnly',
    BELOW_THRESHOLD:     'NearMissBelowThreshold',
    CONFLICTING_EVIDENCE:'NearMissConflictingEvidence',
    PARTITION_MISMATCH:  'NearMissPartitionMismatch',
    ALREADY_MAPPED:      'NearMissAlreadyMapped',
    CLAIMED_BY_STRONGER: 'NearMissClaimedByStronger',
    CLUSTER_FULL:        'NearMissClusterFull',
    AMBIGUOUS_TARGET:    'NearMissAmbiguousTarget',
  };

  static readonly PRIMARY_RULE_IDS: { [code: string]: string } = {
    RECENCY: 'PrimaryRuleRecency',
    ID:      'PrimaryRuleId',
  };

  static readonly ORGANIZATION_STATE_IDS: { [code: string]: string } = {
    NOT_PLANNED:       'MappingStateNotPlanned',
    PLAN_STALE:        'MappingStatePlanStale',
    READY_SIMPLE:      'MappingStateReadySimple',
    READY_PARTIAL:     'MappingStateReadyPartial',
    AWAITING_CUSTOMER: 'MappingStateAwaitingCustomer',
    CUSTOMER_DECIDED:  'MappingStateCustomerDecided',
    COMPLETE:          'MappingStateComplete',
  };

  static readonly BAND_IDS: { [code: string]: string } = {
    SINGLETON: 'MappingBandSingleton',
    CONFIDENT: 'MappingBandConfident',
    REVIEW:    'MappingBandReview',
  };

  /**
   * Strength order, strongest first, taken verbatim from the contract §6.
   * Used to sort members inside a proposal so the SEED and the strongest
   * evidence are read first.
   */
  static readonly JOIN_REASON_STRENGTH: ProjectMappingJoinReason[] = [
    // IDENTICAL outranks everything that follows it: those each name ONE
    // thing that matched, this says they all did.
    'SEED', 'IDENTICAL', 'WORK_ID_EXISTING', 'ISBN_EXACT', 'SFBN_EXACT',
    'BODY_RESOURCE_EXACT', 'BODY_RESOURCE_TITLE', 'BODY_RESOURCE_ONLY',
    'TITLE_AUTHOR_PAGES', 'TITLE_AUTHOR', 'TITLE_EXACT', 'TITLE_CORE',
    'COVER_RESOURCE', 'TITLE_FUZZY'
  ];

  static joinReasonRank(reason: ProjectMappingJoinReason): number {
    const index = ProjectMappingVocabulary.JOIN_REASON_STRENGTH.indexOf(reason);
    return index < 0 ? ProjectMappingVocabulary.JOIN_REASON_STRENGTH.length : index;
  }

  /**
   * `COVER_RESOURCE` corroborates; it never forms a group on its own. Worth
   * saying on screen, because a reader shown "same cover PDF" as the only
   * reason would reasonably conclude the match is weak when in fact that
   * member also matched on something stronger.
   */
  static isCorroborationOnly(reason: ProjectMappingJoinReason): boolean {
    return reason === 'COVER_RESOURCE';
  }

  /**
   * Every §4 state, in workflow order rather than alphabetical: an operator
   * reading the filter list is reading the lifecycle, from "nothing has been
   * done" through to "finished".
   */
  static readonly ORGANIZATION_STATES: ProjectMappingOrganizationState[] = [
    'NOT_PLANNED', 'PLAN_STALE', 'READY_SIMPLE', 'READY_PARTIAL',
    'AWAITING_CUSTOMER', 'CUSTOMER_DECIDED', 'COMPLETE'
  ];

  static readonly ORGANIZATION_STATES_NEEDING_OPERATOR: ProjectMappingOrganizationState[] = [
    'NOT_PLANNED', 'PLAN_STALE', 'READY_SIMPLE', 'READY_PARTIAL', 'CUSTOMER_DECIDED'
  ];
}
